import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import Dashboard from "./pages/dashboard";
import Login from "./pages/login";
import CreateOrder from "./pages/createorder";
import OrderCreation from "./pages/ordercreation";
import WorkflowPlan from "./pages/workflowplanning";
import EditProfile from "./pages/editprofile";
import UserManagement from "./pages/user-management";
import Forgot from "./pages/forgot";
import Reset from "./pages/reset";
import { UserProvider, UserContext } from "./contexts/UserContext";
import { useContext, useEffect } from "react";
import Production from "./pages/productionlist";
import PreProduction from "./pages/pre-productionlist";
import Checkout from "./pages/checkoutlist";
import Completed from "./pages/completedlist";
import NotFoundPage from "./pages/error";
import UnauthorizedPage from "./pages/unauthorizedPage";
import DentistManagement from "./pages/dentist-management";
import StandardNotesManagement from "./pages/standard-notes-management";
import Kpi from "./pages/kpi";
import OrderPickupDropPage from "./pages/orderPickupDropPage";
import ErrorBoundary from "./services/ErrorBoundary";
import CreateDriverOrder from "./pages/createDriverOrder ";
import DriverInBoxOrder from "./pages/DriverInBoxOrder";
import OverViewAllOrders from "./pages/OverViewAllOrders";
import OrdersByDepartment from "./pages/OrdersByDepartment";

// ProtectedRoute Component
function ProtectedRoute({ requiredUrl, children }) {
  const { userData, loading } = useContext(UserContext);

  if (loading) {
    // Optionally, show a loader while data is being fetched
    return <div>Loading...</div>;
  }
  // Redirect unauthenticated users to the login page
  if (!userData) {
    return <Navigate to="/" replace />;
  }
  const userModules = userData?.modules || [];
  const hasAccess = userModules.some(
    (module) =>
      module.url.trim().toLowerCase() === requiredUrl.trim().toLowerCase()
  );

  if (!hasAccess) {
    console.warn(`Access denied to: ${requiredUrl}`);
    return <Navigate to="/unauthorized-access" replace />;
  }

  return children;
}

// AppRoutes Component
function AppRoutes() {
  const { userData, loading } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !userData) {
      const publicPaths = ["/", "/forgot-password", "/reset-password"];
      if (!publicPaths.includes(window.location.pathname)) {
        navigate("/"); // Redirect to login only if not on a public page
      }
    }
  }, [userData, loading, navigate]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Login />} />
      <Route path="/forgot-password" element={<Forgot />} />
      <Route path="/reset-password" element={<Reset />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/edit-profile" element={<EditProfile />} />
      <Route path="/order-creation" element={<OrderCreation />} />
      <Route path="/workflow-planning" element={<WorkflowPlan />} />
      <Route path="/create-driver-order" element={<CreateDriverOrder />} />
      <Route path="/unauthorized-access" element={<UnauthorizedPage />} />
      <Route path="/*" element={<NotFoundPage />} />

      {/* Private Routes */}
      <Route
        path="/create-order"
        element={
          <ProtectedRoute requiredUrl="/create-order">
            <CreateOrder />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pre-production-planning"
        element={
          <ProtectedRoute requiredUrl="/pre-production-planning">
            <PreProduction />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ordersinproduction"
        element={
          <ProtectedRoute requiredUrl="/ordersinproduction">
            <Production />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ordersforcheckout"
        element={
          <ProtectedRoute requiredUrl="/ordersforcheckout">
            <Checkout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/completedorderlist"
        element={
          <ProtectedRoute requiredUrl="/completedorderlist">
            <Completed />
          </ProtectedRoute>
        }
      />

      <Route
        path="/user-management"
        element={
          <ProtectedRoute requiredUrl="/user-management">
            <UserManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dentist-management"
        element={
          <ProtectedRoute requiredUrl="/dentist-management">
            <DentistManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/standard-note-management"
        element={
          <ProtectedRoute requiredUrl="/standard-note-management">
            <StandardNotesManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/kpi"
        element={
          <ProtectedRoute requiredUrl="/kpi">
            <Kpi />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pick-up-and-order-creation"
        element={
          <ProtectedRoute requiredUrl="/pick-up-and-order-creation">
            <OrderPickupDropPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/check-out"
        element={
          <ProtectedRoute requiredUrl="/check-out">
            <DriverInBoxOrder />
          </ProtectedRoute>
        }
      />

      <Route
        path="/overview-all-orders"
        element={
          <ProtectedRoute requiredUrl="/overview-all-orders">
            <OverViewAllOrders />
          </ProtectedRoute>
        }
      />

      <Route
        path="/department-dashboard"
        element={
          <ProtectedRoute requiredUrl="/department-dashboard">
            <OrdersByDepartment />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

// Main App Component
function App() {
  return (
    <UserProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </BrowserRouter>
    </UserProvider>
  );
}

export default App;
